import React from 'react'
import asblData from '../../src/data/content/asblData.json'
import { useTranslation } from 'react-i18next'

interface Asbl {
  name: string
  description: string
  address: string
  phone: string
  email: string
  website: string
}

interface ImportantInfo {
  text: string
  link?: {
    url: string
    text: string
  }
}

interface AsblData {
  title: string
  description: string
  asblList: Asbl[]
  importantInfo: ImportantInfo[]
}

const AsblPage: React.FC = () => {
  const { t } = useTranslation()
  const renderAsbl = (asbl: Asbl) => (
    <div className='bg-grisclair p-4 md:p-6 rounded-lg mb-8'>
      <h3 className='text-lg md:text-xl lg:text-2xl font-semibold text-redpink mb-2'>
        {asbl.name}
      </h3>
      <p className='text-anthracite mb-2'>
        {t(
          `help-center-description.${asbl.name
            .toLowerCase()
            .replace(/ /g, '-')}-description`,
        )}
      </p>
      <p className='text-gray-600 mb-1'>
        <strong>{t('address')} :</strong> {asbl.address}
      </p>
      <p className='text-gray-600 mb-1'>
        <strong>{t('phone')} :</strong> {asbl.phone}
      </p>
      <p className='text-gray-600 mb-1'>
        <strong>{t('email')} :</strong>{' '}
        <a
          href={`mailto:${asbl.email}`}
          className='text-redpink hover:text-redpinkDark transition-colors duration-200'
        >
          {asbl.email}
        </a>
      </p>
      <p className='text-gray-600'>
        <strong>{t('website')} :</strong>{' '}
        <a
          href={asbl.website}
          target='_blank'
          rel='noopener noreferrer'
          className='text-redpink hover:text-redpinkDark transition-colors duration-200'
        >
          {asbl.website}
        </a>
      </p>
    </div>
  )

  return (
    <>
      <div className='bg-redpink text-white uppercase text-center lg:text-2xl py-2 mb-5'>
        {t('asbl-title')}
      </div>
      <article className='mx-auto max-w-7xl px-2 lg:px-0'>
        <section className='mb-8 md:mb-12'>
          <h2 className='text-lg md:text-xl lg:text-2xl font-semibold text-redpink mb-4 md:mb-6'>
            {t('asbl-title')}
          </h2>
          <p className='text-anthracite leading-relaxed mb-6 md:mb-8'>
            {t('asbl-description')}
          </p>
        </section>

        <section className='mb-4 md:mb-8'>
          <div className='bg-grisclair p-4 md:p-6 rounded-lg'>
            <h2 className='text-lg md:text-xl lg:text-2xl font-semibold text-redpink'>
              Police
            </h2>
            <ul className='text-gray-600 leading-relaxed list-inside'>
              <li>{t('help-center-description.police-description')}</li>
              <li>
                <span className='font-bold'>
                  {t('help-center-description.formulaire-contact')} :{' '}
                </span>
                <a
                  href='https://www.police.be/5998/fr/contact/formulaire-de-contact'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-redpink hover:text-redpinkDark transition-colors duration-200'
                >
                  https://www.police.be/5998/fr/contact/formulaire-de-contact
                </a>
              </li>
              <li>
                <span>
                  <b>{t('phone')} :</b> 101
                </span>
              </li>
              <li>
                <span>
                  <b>{t('website')} :</b>{' '}
                  <a
                    href='https://www.police.be/5998/fr/contact/formulaire-de-contact'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-redpink hover:text-redpinkDark transition-colors duration-200'
                  >
                    https://www.police.be
                  </a>
                </span>
              </li>
            </ul>
          </div>
        </section>

        {asblData.asblList.map((asbl, index) => (
          <section key={index}>{renderAsbl(asbl)}</section>
        ))}

        <section className='mb-8 md:mb-12'>
          <h2 className='text-lg md:text-xl lg:text-2xl font-semibold text-redpink mb-4 md:mb-6'>
            {t('important-info')}
          </h2>
          <div className='bg-gray-50 p-4 md:p-6 rounded-lg'>
            <ul className='space-y-3 text-gray-600 text-sm md:text-base'>
              {asblData.importantInfo.map(
                (
                  info: {
                    text: string
                    link?: {
                      url: string
                      text: string
                    }
                  },
                  index: React.Key | null | undefined,
                ) => (
                  <li key={index} className='block sm:flex items-start'>
                    <span className='text-redpink mr-2'>•</span>
                    {t(info.text)}
                    {info.link && (
                      <a
                        href={info.link.url}
                        className='text-redpink hover:text-redpinkDark transition-colors duration-200 ml-1'
                      >
                        {t(info.link.text)}
                      </a>
                    )}
                  </li>
                ),
              )}
            </ul>
          </div>
        </section>
      </article>
    </>
  )
}

export default AsblPage
